import {
  Box,
  Typography,
  Card,
  Grid,
  Chip,
  IconButton,
  TextField,
  Button,
  InputAdornment,
  Paper,
  useMediaQuery,
  Autocomplete,
  Pagination,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SpaIcon from "@mui/icons-material/Spa";
import UploadIcon from "@mui/icons-material/Upload";
import { useNavigate } from "react-router-dom";
import * as XLSX from 'xlsx';
import { 
  getAllFarmersAction,
  getAllZonesAction,
  getAllRegionsAction,
  getAllAreasAction,
  getAllDistrictsAction,
  getAllMandalsAction,
  getAllFarmerCommitmentsAction,
  getAllSeasonsAction,
  getAllVillagesAction,
  getAllDealersAction,
} from '../../store/actions'
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

export default function FarmerManagement() {
  const navigate = useNavigate();
  const dispatch = useDispatch()

  const isMobile = useMediaQuery('(max-width:600px)');

  const { successData } = useSelector((state) => state.getAllFarmersReducer);
  const { successData: zonesData } = useSelector((state) => state.getAllZonesReducer || {});
  const { successData: regionsData } = useSelector((state) => state.getAllRegionsReducer || {});
  const { successData: areasData } = useSelector((state) => state.getAllAreasReducer || {});
  const { successData: districtsData } = useSelector((state) => state.getAllDistrictsReducer || {});
  const { successData: mandalsData } = useSelector((state) => state.getAllMandalsReducer || {});
  const { successData: commitmentsData } = useSelector((state) => state.getAllFarmerCommitmentsReducer || {});
  const { successData: seasonsData } = useSelector((state) => state.getAllSeasonsReducer || {});
  const { successData: villagesData } = useSelector((state) => state.getAllVillagesReducer || {});
  const { successData: dealersData } = useSelector((state) => state.getAllDealersReducer || {});

  // Initialize all state at the top before using them
  const [searchQuery, setSearchQuery] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [districtId, setDistrictId] = useState('');
  const [mandalFilter, setMandalFilter] = useState('');
  const [mandalId, setMandalId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  useEffect(() => {
    // Build query params for farmers endpoint - include districtId and mandalId
    const farmerParamsArr = [];
    if (districtId) farmerParamsArr.push(`districtId=${districtId}`);
    if (mandalId) farmerParamsArr.push(`mandalId=${mandalId}`);
    
    const farmerQueryString = farmerParamsArr.length ? `farmer?${farmerParamsArr.join('&')}` : 'farmer';
    dispatch(getAllFarmersAction({ endPoint: farmerQueryString }));
    
    dispatch(getAllZonesAction());
    dispatch(getAllRegionsAction());
    dispatch(getAllAreasAction());
    dispatch(getAllDistrictsAction());
    dispatch(getAllMandalsAction());
    dispatch(getAllSeasonsAction());
    dispatch(getAllVillagesAction());
    dispatch(getAllDealersAction());

    // Build query params for farmer commitments endpoint - only include status
    const commitmentParamsArr = [];
    if (statusFilter) commitmentParamsArr.push(`status=${statusFilter}`);
    
    const commitmentQueryString = commitmentParamsArr.length ? `farmer-commitment?${commitmentParamsArr.join('&')}` : 'farmer-commitment';
    dispatch(getAllFarmerCommitmentsAction({ endPoint: commitmentQueryString }));
  }, [dispatch, districtId, mandalId, statusFilter]);

  // Map API response to UI-friendly structures
  const farmers = successData?.data?.farmers || [];
  const totalCount = successData?.data?.totalFarmerCount ?? successData?.data?.count ?? farmers.length;
  const totalFarmerAcres = Number(successData?.data?.totalFarmerAcres ?? 0);
  const expectedAcresCount = Number(successData?.data?.expectedAcresCount ?? 0);

  const commitments = commitmentsData?.data || [];

  const stats = [
    { label: 'TOTAL FARMERS', value: totalCount.toLocaleString('en-IN'), icon: <PeopleIcon />, bg: '#EEF4FF' },
    { label: 'TOTAL ACRES', value: totalFarmerAcres.toLocaleString('en-IN'), icon: <SpaIcon />, bg: '#FFF1F1' },
    { label: 'TOTAL COMMITTED ACRES', value: expectedAcresCount.toLocaleString('en-IN'), icon: <AccessTimeIcon />, bg: '#FFF8E6' },
      { label: 'TOTAL ACRES SOWN', value:  successData?.data?.totalPurchaseQty ?? 0, icon: <CheckCircleIcon />, bg: '#E9FBF0' },
  ];

  const rows = farmers.map((f) => ({
    name: f?.name || '-',
    code: f?.geoCode || f?.farmerId || '-',
    farmerId: f?.farmerId,
    id: f?.id,
    phone: f?.mobile || '-',
    acres: (() => {
      const val = Number.parseFloat(f?.totalAcres);
      if (!isNaN(val)) return val % 1 === 0 ? val.toFixed(0) : val.toString();

      const commitmentsList = f?.commitments || [];
      const latestCommitment = commitmentsList.reduce((latest, c) => {
        if (!latest) return c;
        return (c?.commitmentId ?? 0) > (latest?.commitmentId ?? 0) ? c : latest;
      }, null);
      const expectedVal = Number.parseFloat(latestCommitment?.expectedAcre);
      if (isNaN(expectedVal)) return '0';
      return expectedVal % 1 === 0 ? expectedVal.toFixed(0) : expectedVal.toString();
    })(),
    irrigation: f?.irrigationType
      ? `${f.irrigationType}${f?.acresUnderIrrigation ? ` (${f.acresUnderIrrigation} Ac)` : ''}`
      : '-',
    focus: f?.focusCrop || '-',
    crops: f?.majorCrops || [],
    status: ((f?.status || '').toUpperCase() === 'ACTIVE') ? 'Active'
      : ((f?.status || '').toUpperCase().startsWith('PENDING')) ? 'Pending'
        : (f?.status || 'Unknown'),
    zone: f?.zoneName || f?.zone || f?.zoneId || '-',
    region: f?.regionName || f?.region || f?.regionId || '-',
    area: f?.areaName || f?.area || f?.areaId || '-',
    district: f?.districtName || f?.district || f?.districtId || '-',
    mandal: f?.mandalName || f?.mandal || f?.mandalId || '-',
  }));

  // Get location data from API responses or extract from farmers
  const zones = zonesData?.data?.zones || zonesData?.data || [];
  const regions = regionsData?.data?.regions || regionsData?.data || [];
  const areas = areasData?.data?.areas || areasData?.data || [];
  const districts = districtsData?.data?.districts || districtsData?.data || [];
  const mandals = mandalsData?.data?.mandals || mandalsData?.data || [];

  // Helper function to get location ID (handles different API response formats)
  const getLocationId = (location) => {
    return location?.id || location?.zoneId || location?.zone_id || 
           location?.regionId || location?.region_id || 
           location?.areaId || location?.area_id ||
           location?.districtId || location?.district_id || 
           location?.mandalId || location?.mandal_id || 
           location?.locationId || '';
  };

  const getLocationName = (location) => {
    return location?.name || location?.zoneName || location?.zone_name ||
           location?.regionName || location?.region_name || 
           location?.areaName || location?.area_name ||
           location?.districtName || location?.district_name || 
           location?.mandalName || location?.mandal_name || location;
  };

  // Helper to get parent zone ID from nested objects
  const getParentZoneId = (location) => {
    return location?.zoneId || location?.zone_id || 
           location?.zone?.zone_id || location?.zone?.zoneId || '';
  };

  // Helper to get parent region ID from nested objects
  const getParentRegionId = (location) => {
    return location?.regionId || location?.region_id || 
           location?.region?.region_id || location?.region?.regionId || '';
  };

  // Helper to get parent area ID from nested objects
  const getParentAreaId = (location) => {
    return location?.areaId || location?.area_id || 
           location?.area?.area_id || location?.area?.areaId || '';
  };

  // Helper to get parent district ID from nested objects
  const getParentDistrictId = (location) => {
    return location?.districtId || location?.district_id || 
           location?.district?.district_id || location?.district?.districtId || '';
  };

  // Extract zone data with IDs
  const zonesList = zones.length > 0
    ? zones.map(z => ({
        name: getLocationName(z),
        id: getLocationId(z) || z?.zoneId || z?.zone_id || z
      })).filter(z => z.name && z.name !== '-')
    : [...new Set(rows.map(r => r.zone))]
        .filter(z => z !== '-')
        .map(z => ({ name: z, id: z }));

  // Extract regions with parent zone IDs
  const regionsList = regions.length > 0
    ? regions.map(r => ({
        name: getLocationName(r),
        id: getLocationId(r) || r?.regionId || r?.region_id || r,
        parentZoneId: getParentZoneId(r)
      })).filter(r => r.name && r.name !== '-')
    : [...new Set(rows.map(r => r.region))]
        .filter(r => r !== '-')
        .map(r => ({ name: r, id: r, parentZoneId: '' }));

  // Extract areas with parent IDs
  const areasList = areas.length > 0
    ? areas.map(a => ({
        name: getLocationName(a),
        id: getLocationId(a) || a?.areaId || a?.area_id || a,
        parentRegionId: getParentRegionId(a),
        parentZoneId: getParentZoneId(a)
      })).filter(a => a.name && a.name !== '-')
    : [...new Set(rows.map(r => r.area))]
        .filter(a => a !== '-')
        .map(a => ({ name: a, id: a, parentRegionId: '', parentZoneId: '' }));

  // Extract districts with parent IDs
  const districtsList = districts.length > 0
    ? districts.map(d => ({
        name: getLocationName(d),
        id: d?.id || d?.districtId || d?.district_id || getLocationId(d) || d,
        parentAreaId: getParentAreaId(d),
        parentRegionId: getParentRegionId(d),
        parentZoneId: getParentZoneId(d),
        // flat name fields returned directly by the districts API
        areaName: d?.areaName || d?.area_name || '',
        regionName: d?.regionName || d?.region_name || '',
        zoneName: d?.zoneName || d?.zone_name || '',
      })).filter(d => d.name && d.name !== '-')
    : [...new Set(rows.map(r => r.district))]
        .filter(d => d !== '-')
        .map(d => ({ name: d, id: d, parentAreaId: '', parentRegionId: '', parentZoneId: '', areaName: '', regionName: '', zoneName: '' }));

  // Extract mandals with parent IDs
  const mandalsList = mandals.length > 0
    ? mandals.map(m => ({
        name: getLocationName(m),
        id: m?.id || m?.mandalId || m?.mandal_id || getLocationId(m) || m,
        parentDistrictId: getParentDistrictId(m),
        parentAreaId: getParentAreaId(m),
        parentRegionId: getParentRegionId(m),
        parentZoneId: getParentZoneId(m)
      })).filter(m => m.name && m.name !== '-')
    : [...new Set(rows.map(r => r.mandal))]
        .filter(m => m !== '-')
        .map(m => ({ name: m, id: m, parentDistrictId: '', parentAreaId: '', parentRegionId: '', parentZoneId: '' }));

  // Hierarchical filtering logic
  const uniqueZones = zonesList
    .map(z => z.name)
    .filter(name => name && typeof name === 'string')
    .sort();

  // Get zone ID from selected zone name
  const selectedZoneId = zoneFilter ? zonesList.find(z => z.name === zoneFilter)?.id : null;

  const uniqueRegions = (zoneFilter && selectedZoneId
    ? regionsList
        .filter(r => !r.parentZoneId || String(r.parentZoneId) === String(selectedZoneId))
        .map(r => r.name)
        .sort()
    : regionsList.map(r => r.name).sort())
    .filter(name => name && typeof name === 'string');

  // Get region ID from selected region name
  const selectedRegionId = regionFilter ? regionsList.find(r => r.name === regionFilter)?.id : null;

  const uniqueAreas = (regionFilter && selectedRegionId && zoneFilter && selectedZoneId
    ? areasList
        .filter(a => (!a.parentZoneId || String(a.parentZoneId) === String(selectedZoneId)) &&
                     (!a.parentRegionId || String(a.parentRegionId) === String(selectedRegionId)))
        .map(a => a.name)
        .sort()
    : areasList.map(a => a.name).sort())
    .filter(name => name && typeof name === 'string');

  // Get area ID from selected area name
  const selectedAreaId = areaFilter ? areasList.find(a => a.name === areaFilter)?.id : null;

  const uniqueDistricts = (areaFilter && selectedAreaId && regionFilter && selectedRegionId && zoneFilter && selectedZoneId
    ? districtsList
        .filter(d => (!d.parentZoneId || String(d.parentZoneId) === String(selectedZoneId)) &&
                     (!d.parentRegionId || String(d.parentRegionId) === String(selectedRegionId)) &&
                     (!d.parentAreaId || String(d.parentAreaId) === String(selectedAreaId)))
        .map(d => d.name)
        .sort()
    : districtsList.map(d => d.name).sort())
    .filter(name => name && typeof name === 'string');

  // Get district ID from selected district name
  const selectedDistrictIdValue = districtFilter ? districtsList.find(d => d.name === districtFilter)?.id : null;

  const uniqueMandals = (districtFilter && selectedDistrictIdValue && areaFilter && selectedAreaId && regionFilter && selectedRegionId && zoneFilter && selectedZoneId
    ? mandalsList
        .filter(m => (!m.parentZoneId || String(m.parentZoneId) === String(selectedZoneId)) &&
                     (!m.parentRegionId || String(m.parentRegionId) === String(selectedRegionId)) &&
                     (!m.parentAreaId || String(m.parentAreaId) === String(selectedAreaId)) &&
                     (!m.parentDistrictId || String(m.parentDistrictId) === String(selectedDistrictIdValue)))
        .map(m => m.name)
        .sort()
    : mandalsList.map(m => m.name).sort())
    .filter(name => name && typeof name === 'string');

  // Build map: farmer_id -> UI display status from the commitment results
  // Approved (backend) -> Purchased (UI), Pending -> Pending, Rejected -> Rejected
  const commitmentStatusMap = {};
  commitments.forEach(c => {
    const fid = String(c.farmer_id);
    if (!commitmentStatusMap[fid]) {
      commitmentStatusMap[fid] = c.status === 'Approved' ? 'Purchased' : c.status;
    }
  });

  // By default show all active farmers; when status is selected show only farmers
  // whose farmer_id appears in the filtered commitments (location already API-filtered)
  const filteredRows = rows.filter((row) => {
    // Always only show active farmers
    if (row.status !== 'Active') return false;

    // If commitment status is selected, only show farmers who have a matching commitment
    if (statusFilter) {
      const hasCommitment = commitments.some(c =>
        String(c.farmer_id) === String(row.farmerId) ||
        String(c.farmer_id) === String(row.id)
      );
      if (!hasCommitment) return false;
    }

    // Search filter
    let matchesSearch = true;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      matchesSearch = (
        String(row.name).toLowerCase().includes(q) ||
        String(row.code).toLowerCase().includes(q) ||
        String(row.phone).toLowerCase().includes(q) ||
        String(row.focus).toLowerCase().includes(q) ||
        String(row.status).toLowerCase().includes(q)
      );
    }

    return matchesSearch;
  }).map(row => {
    // When status filter is active replace the displayed status with the commitment status
    // so the chip shows Purchased/Pending/Rejected instead of Active
    let displayStatus = row.status; // 'Active' when no filter
    if (statusFilter) {
      const cStatus =
        commitmentStatusMap[String(row.farmerId)] ||
        commitmentStatusMap[String(row.id)];
      if (cStatus) displayStatus = cStatus;
    }
    return { ...row, displayStatus };
  });

  // Reset to page 1 when any filter/search changes
  useEffect(() => { setPage(1); }, [searchQuery, zoneFilter, regionFilter, areaFilter, districtFilter, mandalFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Export commitment details to Excel
const handleExportToExcel = () => {
  const commitmentsList = commitmentsData?.data || [];

  if (!commitmentsList.length) {
    alert('No commitment data to export');
    return;
  }

  // Build a map of seasonId -> season object
  const seasonsList = seasonsData?.data?.seasons || [];
  const seasonsMap = {};
  seasonsList.forEach(s => {
    seasonsMap[String(s.seasonId || s.season_id)] = s;
  });

  // Build a flat map of districtId -> district object (with areaName/regionName/zoneName)
  const districtMap = {};
  districtsList.forEach(d => {
    districtMap[String(d.id)] = d;
  });

  // Build a flat map of mandalId -> mandal object
  const mandalMap = {};
  mandalsList.forEach(m => {
    mandalMap[String(m.id)] = m;
  });

  // Build a flat map of villageId -> village name
  const allVillages = villagesData?.data?.villages || villagesData?.data || [];
  const villageMap = {};
  allVillages.forEach(v => {
    const vid = String(v.villageId || v.village_id || v.id || '');
    if (vid) villageMap[vid] = v.name || v.villageName || v.village_name || '-';
  });

  // Build a flat map of dealerId -> dealer name from full dealers list
  const allDealers = dealersData?.data?.dealers || dealersData?.data || [];
  const dealerMap = {};
  allDealers.forEach(d => {
    const did = String(d.dealerId || d.dealer_id || d.id || '');
    if (did) dealerMap[did] = d.name || '-';
  });

  // Build a map of farmerId -> { createdByName, createdAt } from the farmers list
  const allFarmersRaw = successData?.data?.farmers || [];
  const farmerMetaMap = {};
  allFarmersRaw.forEach(f => {
    const fid = String(f.farmerId || f.id || '');
    if (fid) {
      farmerMetaMap[fid] = {
        createdByName: f.createdByName || '-',
        createdAt: f.createdAt || '',
      };
    }
  });

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '-';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const exportData = commitmentsList.map((commitment) => {
    const farmer = commitment.farmer || {};
    const crop = commitment.crop || {};
    const variety = commitment.variety || {};

    // Resolve season
    const seasonObj = commitment.season || {};
    const resolvedSeason =
      seasonsMap[String(seasonObj.seasonId || seasonObj.season_id || commitment.season_id)] ||
      seasonObj;
    const seasonName = resolvedSeason?.name || '-';
    const seasonYear = resolvedSeason?.year || '-';

    // Resolve location names from district lookup (district API returns areaName, regionName, zoneName flat)
    const districtId = String(farmer.district_id || '');
    const mandalId = String(farmer.mandal_id || '');
    const villageId = String(farmer.village_id || '');

    const districtObj = districtMap[districtId] || {};
    const districtName = districtObj.name || '-';
    const areaName = districtObj.areaName || '-';
    const regionName = districtObj.regionName || '-';
    const zoneName = districtObj.zoneName || '-';

    const mandalObj = mandalMap[mandalId] || {};
    const mandalName = mandalObj.name || '-';

    const villageName = villageMap[villageId] || '-';

    // Resolve purchased dealer name: prefer nested object, then full dealer list by ID
    const purchasedDealerObj = commitment.purchased_dealer || {};
    const purchasedDealerId = String(commitment.purchased_dealer_id || '');
    const purchasedDealerName =
      purchasedDealerObj.name ||
      (purchasedDealerId ? dealerMap[purchasedDealerId] : null) ||
      '-';

    // Resolve expected dealer names from expected_dealer_id array
    const expectedDealerIds = Array.isArray(commitment.expected_dealer_id)
      ? commitment.expected_dealer_id
      : commitment.expected_dealer_id
        ? [commitment.expected_dealer_id]
        : [];
    const expectedDealerNames = expectedDealerIds
      .map(id => dealerMap[String(id)] || String(id))
      .join(', ') || '-';

    // Resolve created-by info from farmer meta map (fallback to nested farmer object)
    const farmerIdKey = String(farmer.farmerId || farmer.farmer_id || commitment.farmer_id || '');
    const farmerMeta = farmerMetaMap[farmerIdKey] || {};
    const createdByName = farmer.createdByName || farmerMeta.createdByName || '-';
    const createdAt = farmer.createdAt || farmerMeta.createdAt || '';

    return {
      'Created Date': formatDate(createdAt),
      'Created Employee': createdByName,
      'Farmer Name': farmer.name || '-',
      'Mobile Number': farmer.mobile || '-',
      'Zone': zoneName,
      'Region': regionName,
      'Area': areaName,
      'District': districtName,
      'Mandal': mandalName,
      'Village': villageName,
      'Total Acres': farmer.total_acres || '-',
      'Crop Name': crop.name || '-',
      'Variety Name': variety.name || commitment.variety_name || '-',
      'Committed Acres': commitment.expected_acre || '-',
      'Expected Qty': commitment.expected_qty || '-',
      'Expected Dealer': expectedDealerNames,
      'Purchased Qty': commitment.purchase_qty || '-',
      'Purchased Dealer': purchasedDealerName,
      'Season': seasonName !== '-' && seasonYear !== '-' ? `${seasonName} ${seasonYear}` : seasonName !== '-' ? seasonName : seasonYear !== '-' ? seasonYear : '-',
      'Status': commitment.status === 'Approved' ? 'Purchased' : commitment.status || '-',
    };
  });

  const ws = XLSX.utils.json_to_sheet(exportData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Farmer Commitments');

  XLSX.writeFile(wb, `Farmer_Commitments.xlsx`);
};

  return (
    <Box sx={{ minHeight: "100vh", }}>
      {/* Header */}
      <Box mb={1.5} mt={{ xs: 0, sm: 10, md: 0 }}>
        <Typography variant="h6" fontWeight={600}  >
          Farmer Management
        </Typography>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} mb={3}>
        {stats.map((s) => (
          <Grid item size={{ xs: 6, sm: 3, md: 3 }} key={s.label}>
            <Card
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid #E5E7EB',
                height: { xs: 75, sm: 75,md: 82,lg:60 },
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {s.label}
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {s.value}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: s.bg,
                    p: 1.2,
                    borderRadius: 2,
                  }}
                >
                  {s.icon}
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Farmer Search & Filters */}
      <Card
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          bgcolor: "#fff",
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          boxShadow: 'none',
        }}
      >
        {/* Main Grid Layout */}
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "1fr 1fr",
            sm: "1fr 1fr 1fr",
            md: "1fr 1fr 1fr 1fr 1fr 1fr 1fr",
          }}
          gap={2}
          alignItems="end"
        >
          {/* Search Bar */}
          <TextField
            label="Search Farmers"
            placeholder="Name, Phone, Code, Crop..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            sx={{
              bgcolor: '#fff',
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                '&:hover fieldset': { borderColor: '#5B4CF5' },
                '&.Mui-focused fieldset': { borderColor: '#5B4CF5' },
              }
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: 3 }
              }
            }}
          />

          {/* Zone Filter */}
          <Autocomplete
            size="small"
            fullWidth
            options={uniqueZones}
            value={zoneFilter || null}
            onChange={(event, newValue) => {
              setZoneFilter(newValue || '');
              // Clear dependent filters when zone changes
              setRegionFilter('');
              setAreaFilter('');
              setDistrictFilter('');
              setDistrictId('');
              setMandalFilter('');
              setMandalId('');
            }}
            filterOptions={(options, state) =>
              options.filter(option =>
                option.toLowerCase().includes(state.inputValue.toLowerCase())
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Zone"
                placeholder={`Search zones (${uniqueZones.length})...`}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    '&:hover fieldset': { borderColor: '#5B4CF5' },
                    '&.Mui-focused fieldset': { borderColor: '#5B4CF5' },
                  }
                }}
              />
            )}
            noOptionsText="No zones found"
            isOptionEqualToValue={(option, value) => option === value}
          />

          {/* Region Filter */}
          <Autocomplete
            size="small"
            fullWidth
            options={uniqueRegions}
            value={regionFilter || null}
            onChange={(event, newValue) => {
              setRegionFilter(newValue || '');
              // Clear dependent filters when region changes
              setAreaFilter('');
              setDistrictFilter('');
              setDistrictId('');
              setMandalFilter('');
              setMandalId('');
            }}
            filterOptions={(options, state) =>
              options.filter(option =>
                option.toLowerCase().includes(state.inputValue.toLowerCase())
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Region"
                placeholder={`Search regions (${uniqueRegions.length})...`}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    '&:hover fieldset': { borderColor: '#5B4CF5' },
                    '&.Mui-focused fieldset': { borderColor: '#5B4CF5' },
                  }
                }}
              />
            )}
            noOptionsText="No regions found"
            isOptionEqualToValue={(option, value) => option === value}
          />

          {/* Area Filter */}
          <Autocomplete
            size="small"
            fullWidth
            options={uniqueAreas}
            value={areaFilter || null}
            onChange={(event, newValue) => {
              setAreaFilter(newValue || '');
              // Clear dependent filters when area changes
              setDistrictFilter('');
              setDistrictId('');
              setMandalFilter('');
              setMandalId('');
            }}
            filterOptions={(options, state) =>
              options.filter(option =>
                option.toLowerCase().includes(state.inputValue.toLowerCase())
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Area"
                placeholder={`Search areas (${uniqueAreas.length})...`}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    '&:hover fieldset': { borderColor: '#5B4CF5' },
                    '&.Mui-focused fieldset': { borderColor: '#5B4CF5' },
                  }
                }}
              />
            )}
            noOptionsText="No areas found"
            isOptionEqualToValue={(option, value) => option === value}
          />

          {/* District Filter */}
          <Autocomplete
            size="small"
            fullWidth
            options={uniqueDistricts}
            value={districtFilter || null}
            onChange={(event, newValue) => {
              setDistrictFilter(newValue || '');
              // Get district ID and clear mandal when district changes
              if (newValue) {
                const selectedDistrict = districtsList.find(d => d.name === newValue);
                setDistrictId(selectedDistrict?.id || '');
              } else {
                setDistrictId('');
              }
              setMandalFilter('');
              setMandalId('');
            }}
            filterOptions={(options, state) =>
              options.filter(option =>
                option.toLowerCase().includes(state.inputValue.toLowerCase())
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="District"
                placeholder={`Search districts (${uniqueDistricts.length})...`}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    '&:hover fieldset': { borderColor: '#5B4CF5' },
                    '&.Mui-focused fieldset': { borderColor: '#5B4CF5' },
                  }
                }}
              />
            )}
            noOptionsText="No districts found"
            isOptionEqualToValue={(option, value) => option === value}
          />

          {/* Mandal Filter */}
          <Autocomplete
            size="small"
            fullWidth
            options={uniqueMandals}
            value={mandalFilter || null}
            onChange={(event, newValue) => {
              setMandalFilter(newValue || '');
              // Get mandal ID when mandal is selected
              if (newValue) {
                const selectedMandal = mandalsList.find(m => m.name === newValue);
                setMandalId(selectedMandal?.id || '');
              } else {
                setMandalId('');
              }
            }}
            filterOptions={(options, state) =>
              options.filter(option =>
                option.toLowerCase().includes(state.inputValue.toLowerCase())
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Mandal"
                placeholder={`Search mandals (${uniqueMandals.length})...`}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    '&:hover fieldset': { borderColor: '#5B4CF5' },
                    '&.Mui-focused fieldset': { borderColor: '#5B4CF5' },
                  }
                }}
              />
            )}
            noOptionsText="No mandals found"
            isOptionEqualToValue={(option, value) => option === value}
          />

          {/* Status Filter */}
          <Autocomplete
            size="small"
            fullWidth
            options={['Purchased', 'Pending', 'Rejected']}
            value={statusFilter ? (statusFilter === 'Approved' ? 'Purchased' : statusFilter) : null}
            onChange={(event, newValue) => {
              const valueMap = { 'Purchased': 'Approved', 'Pending': 'Pending', 'Rejected': 'Rejected' };
              setStatusFilter(valueMap[newValue] || '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Status"
                placeholder="Select status..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    '&:hover fieldset': { borderColor: '#5B4CF5' },
                    '&.Mui-focused fieldset': { borderColor: '#5B4CF5' },
                  }
                }}
              />
            )}
            noOptionsText="No status options"
            isOptionEqualToValue={(option, value) => option === value}
          />

          {/* Export Button */}
          <Button
            variant="outlined"
            startIcon={<UploadIcon />} // Changed to Upload icon
            onClick={handleExportToExcel}
            sx={{
              gridColumn: { md: '5' },
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 600,
              height: '40px',
              borderColor: '#5B4CF5',
              color: '#5B4CF5',
              minWidth: { xs: 90, sm: 100, md: 110, lg: 120 },
              fontSize: { xs: '0.85rem', sm: '0.90rem', md: '0.90rem', lg: '0.85rem', xl: '0.95rem' },
              whiteSpace: 'nowrap',
              '&:hover': {
                borderColor: "#4C3FD5",
                backgroundColor: "#F5F3FF"
              }
            }}
          >
            Export
          </Button>

          {/* Add New Farmer Button */}
          <Button
            variant="contained"
            sx={{
              gridColumn: { md: '6' },
              bgcolor: "#5B4CF5",
              px: { xs: 1.5, sm: 2, md: 2, lg: 1.5, xl: 2 },
              py: { xs: 1, sm: 1, md: 1, lg: 0.8, xl: 1 },
              borderRadius: 3,
              textTransform: "none",
              fontSize: { xs: '0.80rem', sm: '0.85rem', md: '0.80rem', lg: '0.70rem', xl: '0.90rem' },
              fontWeight: 600,
              boxShadow: 'none',
              height: '40px',
              width: "100%",
              minWidth: { xs: 120, sm: 130, md: 140, lg: 120, xl: 150 },
              whiteSpace: 'nowrap',
              letterSpacing: 0,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              '&:hover': {
                bgcolor: "#4C3FD5",
                boxShadow: '0 4px 12px rgba(91, 76, 245, 0.3)',
              }
            }}
            onClick={() => navigate('/masterData/createFarmer')}
          >
            + Register Farmer
          </Button>
           <Button
            variant="contained"
            sx={{
              gridColumn: { md: '7' },
              bgcolor: "#5B4CF5",
              px: { xs: 1.5, sm: 2, md: 2, lg: 1.5, xl: 2 },
              py: { xs: 1, sm: 1, md: 1, lg: 0.8, xl: 1 },
              borderRadius: 3,
              textTransform: "none",
              fontSize: { xs: '0.80rem', sm: '0.85rem', md: '0.80rem', lg: '0.70rem', xl: '0.90rem' },
              fontWeight: 600,
              boxShadow: 'none',
              height: '40px',
              width: "100%",
              minWidth: { xs: 120, sm: 130, md: 140, lg: 120, xl: 150 },
              whiteSpace: 'nowrap',
              letterSpacing: 0,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              '&:hover': {
                bgcolor: "#4C3FD5",
                boxShadow: '0 4px 12px rgba(91, 76, 245, 0.3)',
              }
            }}
            onClick={() => navigate('/masterData/salesRegister')}
          >
            + Sales Register
          </Button>
        </Box>
      </Card>

      {/* Responsive List/Table */}
      {isMobile ? (
        <Box>
          {pagedRows.map((row) => (
            <Card key={row.code} sx={{ mb: 2, borderRadius: 3, p: 2 }}>
              {/* Farmer and Status */}
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                <Box>
                  <Typography>
                    <span style={{ fontWeight: 600 }}>Farmer:</span> {row.name}
                  </Typography>
                </Box>
                <Chip
                  label={row.displayStatus}
                  sx={{
                    width: 90,
                    backgroundColor:
                      row.displayStatus === "Active" || row.displayStatus === "Purchased"
                        ? "#D1FAE5"
                        : row.displayStatus === "Pending"
                          ? "#FFEFD6"
                          : row.displayStatus === "Rejected"
                            ? "#FEE2E2"
                            : "default",
                    color:
                      row.displayStatus === "Active" || row.displayStatus === "Purchased"
                        ? "#047857"
                        : row.displayStatus === "Pending"
                          ? "#B45309"
                          : row.displayStatus === "Rejected"
                            ? "#DC2626"
                            : undefined,
                    fontWeight: 600,
                  }}
                  variant="filled"
                  size="small"
                />
              </Box>

              {/* Phone */}
              <Box mb={0.5}>
                <Typography variant="body2"><strong>Phone:</strong> {row.phone}</Typography>
              </Box>
              {/* Agriculture */}
              <Box mb={0.5}>
                <Typography variant="body2"><strong>Agriculture:</strong> {row.acres} Acres</Typography>
              </Box>
              {/* Crop */}
              <Box mb={0.5}>
                <Typography variant="body2"><strong>Crop:</strong> {row.focus}</Typography>
              </Box>

              {/* Actions */}
              <Box display="flex" justifyContent="flex-end" mt={1} gap={0.5}>
                
                <IconButton
                  size="small"
                  aria-label="edit"
                  onClick={() => navigate('/masterData/farmer/create', { state: { farmerId: row.farmerId } })}
                >
                  <EditOutlinedIcon fontSize="small" sx={{ color: "#2563EB", filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                </IconButton>
              </Box>
            </Card>
          ))}
          {filteredRows.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>No farmers found.</Typography>
          )}
          {totalPages > 1 && (
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={1}>
              <Typography variant="body2" >
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredRows.length)} to {Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length} entries
              </Typography>
              <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" size="small" />
            </Box>
          )}
        </Box>
      ) : (
        <Box>
          {/* Table Header: visible only on sm and up */}
          <Box
            display={{ xs: "none", sm: "grid" }}
            gridTemplateColumns={{ sm: "2fr 2fr 2fr 1fr 1fr 1fr" }}
            px={{ xs: 1, sm: 2 }}
            mb={{ xs: 1, sm: 1 }}
          >
            {[
              "Farmer Identity",
              "Contact & Location",
              "Agricultural Info",
              "Crop",
              "Status",
              "Actions",
            ].map((h) => (
              <Typography key={h} fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>
                {h.toUpperCase()}
              </Typography>
            ))}
          </Box>

          {/* Rows */}
          {pagedRows.map((row) => (
            <Paper
              key={row.code}
              elevation={0}
              sx={{
                mb: { xs: 1, sm: 2 },
                p: { xs: 1, sm: 1.5 },
                borderRadius: 3,
                display: { xs: "block", sm: "grid" },
                gridTemplateColumns: { sm: "2fr 2fr 2fr 1fr 1fr 1fr" },
                alignItems: "center",
                border: "1px solid #E5E7EB",
                gap: { xs: 1, sm: 0 },
                '&:hover': {
                  border: '1.5px solid #2563EB',
                  boxShadow: '0 4px 16px 0 rgba(37,99,235,0.15)',
                },
              }}
            >
              {/* Farmer Identity */}
              <Box display={{ xs: "none", sm: "flex" }} gap={2} alignItems="center">
                <Box>
                  <Typography fontWeight={500} fontSize={{ sm: 16 }}>{row.name}</Typography>
                </Box>
              </Box>

              {/* Contact & Location */}
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Typography variant="body2">{row.phone}</Typography>
              </Box>

              {/* Agricultural Info */}
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Typography fontWeight={600} fontSize={{ sm: 14 }}>
                  {row.acres} <span style={{ fontWeight: 400 }}>Acres</span>
                </Typography>
              </Box>

              {/* Crop */}
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Typography variant="body2">{row.focus}</Typography>
              </Box>

              {/* Status */}
              <Chip
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  width: { sm: 90 },
                  backgroundColor:
                    row.displayStatus === "Active" || row.displayStatus === "Purchased"
                      ? "#D1FAE5"
                      : row.displayStatus === "Pending"
                        ? "#FFEFD6"
                        : row.displayStatus === "Rejected"
                          ? "#FEE2E2"
                          : "default",
                  color:
                    row.displayStatus === "Active" || row.displayStatus === "Purchased"
                      ? "#047857"
                      : row.displayStatus === "Pending"
                        ? "#B45309"
                        : row.displayStatus === "Rejected"
                          ? "#DC2626"
                          : undefined,
                  fontWeight: 600,
                }}
                label={row.displayStatus}
                size="small"
              />

              {/* Actions */}
              <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 1, justifyContent: "flex-start" }}>
                <IconButton
                  size="small"
                  onClick={() => navigate('/masterData/createFarmer', { state: { farmerId: row.farmerId } })}
                >
                  <EditOutlinedIcon fontSize="small" sx={{ color: "#2563EB", filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                </IconButton>
              </Box>
            </Paper>
          ))}
          {filteredRows.length === 0 && (
            <Typography variant="body2"  color="text.secondary" sx={{ px: 2 }}>No farmers found.</Typography>
          )}
          {totalPages > 1 && (
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={1}>
              <Typography variant="body2" >
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredRows.length)} to {Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length} entries
              </Typography>
              <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
